import { GridRowData } from '@material-ui/data-grid';
import { Extent } from 'ol/extent';
import {
  createContext,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react';
import { LocationDisplayType, LocationSearchData } from '../Toolbar/Location/LocationSearch';
import {
  AssetFilter,
  ActiveGeofence,
  GeofenceFilter,
  Asset,
  SearchType,
  LastKnownLocation,
  NameId,
} from '../types';
import { addWeeks, compareDesc } from 'date-fns';
import {
  AssetEntity,
  EntityType,
  FenceEntityType,
  FenceZone,
  isGeofenceEntityType,
  MapType,
  SearchTypeIDs,
  SearchTypeValue,
} from '../../../util/enums';
import { useAtomValue } from 'jotai';
import { KNOWN_TOOLS } from '../../../store/tools';
import { setActiveMicrofences, setMicrofencetoAssets, useMapData } from '../useMapData';
import { AssetState, AssetUpdateType, IdentifiersAndLabel } from '../../../util/Events/Messages';
import { createFeatureFromTypeAndCoordinates, typeAndIdToAssetStateKey } from '../Helpers';
import { stringifyIdRecord } from '../../../util/stringUtils';
import { ProjectlessFenceIdentifiers } from '../../../util/Events/schema';
import API from '../../../API/api';
import { CID, PID } from '../../../store/user';
import { ACCESS_JWT_TOKEN } from '../../../store/auth';
import { transformExtent } from 'ol/proj';
import { ALL_GROUPS, ALL_LAYERS, UNKNOWN } from '../../../util/constants';
import { geometryTypeOfEntity } from '../commons';
import { ExtendedMap } from '../../../util/mapUtils';
import { SearchTypes } from '../values';
import { LiveReplayMapProps, MapState } from './Props';
import {
  SelectedEntity,
  assetSelection,
  toolSelection,
  geofenceSelection,
  microfenceSelection,
  activeAssetSelection,
  selectedGeofence as getSelectedGeofence,
  selectedActiveAsset as getSelectedActiveAsset,
  isSomethingSelected,
  deselectEntity,
  withActiveAsset,
} from './Selection';
import { PROMISE_VOID, VOID } from '../../../util/typeUtils';
import { useLiveMapLoader } from '../../../hooks/geomoby/useLiveMapLoader';
import { PagiantedMicrofencesQuery } from '../../../API/types/triggers';
import { isDefined } from '../../../util/isDefined';

const isIdentifierRecord = (value: unknown): value is Record<string, string | undefined> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(id => id === undefined || typeof id === 'string');

const isAssetState = (entity: Asset | AssetState | GridRowData): entity is AssetState =>
  isIdentifierRecord(entity.id);

const isAsset = (entity: Asset | AssetState | GridRowData): entity is Asset =>
  typeof entity.id === 'string' &&
  typeof entity.label === 'string' &&
  typeof entity.type === 'string';

type SelectableFenceRow = GridRowData & {
  assetId?: Record<string, string | undefined>;
  point?: { coordinates?: number[] };
};

const isSelectableFenceRow = (
  entity: Asset | AssetState | GridRowData,
): entity is SelectableFenceRow => {
  if (typeof entity.id !== 'string') return false;
  if ('point' in entity && entity.point !== undefined && entity.point !== null) {
    if (typeof entity.point !== 'object') return false;
    if (
      'coordinates' in entity.point &&
      (!Array.isArray(entity.point.coordinates) ||
        !entity.point.coordinates.every((coordinate: unknown) => typeof coordinate === 'number'))
    ) {
      return false;
    }
  }
  return (
    !('assetId' in entity) || entity.assetId === undefined || isIdentifierRecord(entity.assetId)
  );
};

export const LiveReplayMapContext = createContext<LiveReplayMapProps>({
  layers: [],
  microfences: [],
  selected: undefined,
  select: VOID,
  selectedStringifiedGeofenceEventData: undefined,
  setSelectedStringifiedGeofenceEventData: VOID,
  searchType: undefined,
  setSearchType: VOID,
  selectedLayer: undefined,
  setSelectedLayer: VOID,
  assetFilter: undefined,
  setAssetFilter: VOID,
  toolFilter: undefined,
  setToolFilter: VOID,
  geofenceFilter: undefined,
  setGeofenceFilter: VOID,
  microfenceFilter: undefined,
  setMicrofenceFilter: VOID,
  clearFilter: false,
  setClearFilter: VOID,
  showFilter: false,
  setShowFilter: VOID,
  refreshSearch: false,
  setRefreshSearch: VOID,
  activeAssets: new ExtendedMap<string, AssetState>(),
  activeGeofence: undefined,
  setActiveGeofence: VOID,
  setSelectedActiveAsset: VOID,
  setLocationSearchData: VOID,
  currentCenter: undefined,
  locationDisplay: undefined,
  setLocationDisplay: VOID,
  setNavigate: VOID,
  userExtent: undefined,
  getNowIso8601: () => new Date().toISOString(),
  lastUpdate: undefined,
  setUserExtent: VOID,
  liveMapStaticData: undefined,
  mapState: undefined,
  setMapState: VOID,
  mapType: MapType.LIVE_MAP,
  resetStateRef: { current: () => {} },
  onExtentChanged: VOID,
  fencesLoading: false,
  locationSearchData: undefined,
  setCurrentCenter: VOID,
  navigate: false,
  deselectFence: false,
  setDeselectFence: VOID,
  externallySetLastLocation: undefined,
  setExternallySetLastLocation: VOID,
  expandEventCategory: undefined,
  setExpandEventCategory: VOID,
  getGeofence: PROMISE_VOID,
  onCategorySelected: VOID,
  onEntitySelected: VOID,
  onClickedBack: VOID,
  range: [null, null],
  setRange: VOID,
  onClickedAssetEventLink: PROMISE_VOID,
});

export const LiveReplayMapProvider = ({
  mapType,
  children,
}: {
  mapType: MapType;
  children: ReactNode;
}) => {
  const streamData = useMapData(); // Assets
  const mapData = useLiveMapLoader(); // Fences
  const knownTools = useAtomValue(KNOWN_TOOLS);
  const liveMapStaticData = mapData.result;
  const onExtentChanged = mapData.setBounds;
  const fencesLoading = mapData.loading;

  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);
  const accessJwtToken = useAtomValue(ACCESS_JWT_TOKEN);
  const resetStateRef = useRef(() => {});
  const [expandEventCategory, setExpandEventCategory] = useState<AssetUpdateType | undefined>();
  const [externallySetLastLocation, setExternallySetLastLocation] = useState<
    LastKnownLocation | undefined
  >();
  const [activeGeofence, setActiveGeofence] = useState<ActiveGeofence | undefined>();
  const [locationDisplay, setLocationDisplay] = useState<LocationDisplayType>();
  const [clearFilter, setClearFilter] = useState<boolean>(false);
  const [assetFilter, setAssetFilter] = useState<AssetFilter | undefined>();
  const [currentCenter, setCurrentCenter] = useState<number[] | undefined>();
  const [deselectFence, setDeselectFence] = useState<boolean>(false);
  const [geofenceFilter, setGeofenceFilter] = useState<GeofenceFilter | undefined>();
  const [microfenceFilter, setMicrofenceFilter] = useState<PagiantedMicrofencesQuery | undefined>();
  const [locationSearchData, setLocationSearchData] = useState<LocationSearchData | undefined>();
  const [mapState, setMapState] = useState<MapState | undefined>();
  const [navigate, setNavigate] = useState<boolean>(false);
  const [range, setRange] = useState<[Date | null, Date | null]>([
    addWeeks(new Date(), -1),
    new Date(),
  ]);
  const [refreshSearch, setRefreshSearch] = useState<boolean>(false);
  const [searchType, setSearchType] = useState<SearchType | undefined>();
  const [selected, setSelected] = useState<SelectedEntity | undefined>();
  const [selectedLayer, setSelectedLayer] = useState<NameId | undefined>();
  const [selectedStringifiedGeofenceEventData, setSelectedStringifiedGeofenceEventData] = useState<
    string | undefined
  >();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [toolFilter, setToolFilter] = useState<AssetFilter | undefined>();
  const [userExtent, setUserExtent] = useState<Extent | undefined>();

  const activeAssets = useMemo(() => {
    return streamData.liveData?.state.assets ?? new ExtendedMap<string, AssetState>();
  }, [streamData.liveData?.state.assets]);

  const lastUpdate = useMemo(
    () => streamData.liveData?.state.lastUpdate,
    [streamData.liveData?.state.lastUpdate],
  );

  const lastStreamed = useMemo(
    () => streamData.liveData?.state.lastStreamed,
    [streamData.liveData?.state.lastStreamed],
  );

  const microfences = useMemo(
    () => liveMapStaticData?.microfences ?? [],
    [liveMapStaticData?.microfences],
  );

  const layers = useMemo(() => liveMapStaticData?.layers ?? [], [liveMapStaticData?.layers]);

  const somethingSelected = useMemo(() => isSomethingSelected(selected), [selected]);

  const select = useCallback(function selectEntity(entity: SelectedEntity | undefined) {
    setSelected(entity);
  }, []);

  // Focused accessor into the active-asset portion of the current selection.
  // Supports partial/functional updates (stream refreshes, recentLocations
  // merges). With no current selection, a value creates a lone active-asset
  // selection, preserving the previous standalone-selectedActiveAsset behaviour.
  const setSelectedActiveAsset = useCallback(function setSelectedActiveAssetValue(
    value: AssetState | undefined | ((prev: AssetState | undefined) => AssetState | undefined),
  ) {
    setSelected(prev => {
      const current = getSelectedActiveAsset(prev);
      const next = typeof value === 'function' ? value(current) : value;
      if (!prev) {
        return next ? activeAssetSelection(next) : undefined;
      }
      return withActiveAsset(prev, next);
    });
  },
  []);

  const getGeofence = useCallback(
    async function fetchGeofence(geofence: GridRowData) {
      const type = geometryTypeOfEntity(geofence);
      if (!isGeofenceEntityType(type)) return;

      return API.triggers.getGeofence({
        id: geofence.id,
        layerId: geofence.layerId,
        type,
        projectId: pid,
        clientId: cid,
        Authorization: `Bearer ${accessJwtToken}`,
      });
    },
    [cid, pid, accessJwtToken],
  );

  const deselectAllEntities = useCallback(function deselectEntities(
    exclude?: EntityType | EntityType[],
  ) {
    const preserveTypes = exclude === undefined ? [] : Array.isArray(exclude) ? exclude : [exclude];

    setSelected(previous => deselectEntity(previous, preserveTypes));
    if (!preserveTypes.includes(EntityType.Layer)) {
      setSelectedLayer(undefined);
    }
    setDeselectFence(true);
  },
  []);

  const setExtentFromCoords = (coords?: number[]) => {
    if (!coords) return;
    const extent = transformExtent([...coords, ...coords], 'EPSG:4326', 'EPSG:3857');
    setUserExtent(extent);
  };

  const setAssetFromAssetState = useCallback(
    function setAssetFromAssetState(assetState: AssetState) {
      switch (assetState.type) {
        case AssetEntity.Beacon:
          if (!assetState.id?.beaconId) return;
          select(
            assetSelection(
              EntityType.Beacon,
              {
                id: assetState.id.beaconId,
                label: assetState.label ?? '',
                type: AssetEntity.Beacon,
              },
              assetState,
            ),
          );
          break;

        case AssetEntity.Device:
          if (!assetState.id?.deviceId) return;
          select(
            assetSelection(
              EntityType.Device,
              {
                id: assetState.id.deviceId,
                label: assetState.label ?? '',
                type: AssetEntity.Device,
              },
              assetState,
            ),
          );
          break;

        case AssetEntity.GpsTracker:
          if (!assetState.id?.gpsTrackerId) return;
          select(
            assetSelection(
              EntityType.GpsTracker,
              {
                id: assetState.id.gpsTrackerId,
                label: assetState.label ?? '',
                type: AssetEntity.GpsTracker,
              },
              assetState,
            ),
          );
          break;

        case AssetEntity.Tool:
          if (!assetState.tool) return;
          select(toolSelection(assetState.tool, assetState));
          break;

        default:
          return;
      }
    },
    [select],
  );

  const onEntitySelected = useCallback(
    async function onEntitySelected(entity: Asset | AssetState | GridRowData) {
      if (!searchType) return;

      // Active Assets
      if (searchType.id === SearchTypeIDs.ActiveAssets) {
        if (!isAssetState(entity)) return;
        if (entity.microfence) {
          select(microfenceSelection(entity.microfence, entity));
          setExtentFromCoords(entity.microfence.point?.coordinates);
          return;
        }
        setAssetFromAssetState(entity);
        return;
      }

      // Geofences & Microfences
      if (
        searchType.id === SearchTypeIDs.Geofences ||
        searchType.id === SearchTypeIDs.Microfences
      ) {
        if (!isSelectableFenceRow(entity)) return;
        const rowData = entity;

        if (searchType.id === SearchTypeIDs.Geofences) {
          const foundLayer = [{ id: ALL_LAYERS, name: ALL_GROUPS }, ...layers].find(
            layer => layer.id === entity.id,
          );
          if (selectedLayer) {
            select(geofenceSelection(rowData));
            const foundGeofence = await getGeofence(rowData);
            if (!foundGeofence) return;
            const newFence = createFeatureFromTypeAndCoordinates(foundGeofence);
            setUserExtent(transformExtent(newFence.getExtent(), 'EPSG:4326', 'EPSG:3857'));
            return;
          } else if (foundLayer) {
            setDeselectFence(true);
            setSelectedLayer(foundLayer);
            setClearFilter(true);
          }
          return;
        }

        // Microfences
        const assetId = rowData.assetId;
        if (!assetId) return;
        setExtentFromCoords(rowData.point?.coordinates);

        const activeMicrofence = activeAssets
          .toArray()
          .find(
            (asset: AssetState) =>
              asset.microfence && stringifyIdRecord(asset.id) === stringifyIdRecord(assetId),
          );

        select(microfenceSelection(rowData, activeMicrofence));
        return;
      }

      // Specific asset types
      if (!isAsset(entity)) return;
      switch (searchType.id) {
        case SearchTypeIDs.Beacons:
          select(assetSelection(EntityType.Beacon, entity));
          break;
        case SearchTypeIDs.Devices:
          select(assetSelection(EntityType.Device, entity));
          break;
        case SearchTypeIDs.GPSTrackers:
          select(assetSelection(EntityType.GpsTracker, entity));
          break;
        case SearchTypeIDs.Tools:
          select(toolSelection(entity));
          break;
      }
    },
    [activeAssets, getGeofence, searchType, layers, selectedLayer, select, setAssetFromAssetState],
  );

  const onCategorySelected = useCallback(
    function onCategorySelected(category: EntityType) {
      const foundSearchType = SearchTypes.find(search => search.id === `${category}S`);
      if (!foundSearchType) return;
      const layerId =
        foundSearchType.id === SearchTypeIDs.Geofences
          ? getSelectedGeofence(selected)?.layerId
          : undefined;
      setSearchType(foundSearchType);
      deselectAllEntities();
      if (layerId) {
        setSelectedLayer({
          id: layerId,
          name: layers?.find(lyr => lyr.id === layerId)?.name ?? UNKNOWN,
        });
      }
      setRefreshSearch(true);
    },
    [deselectAllEntities, layers, selected],
  );

  const onClickedBack = useCallback(
    function onClickedBack() {
      deselectAllEntities(EntityType.Layer);
      setDeselectFence(true);
      setUserExtent(undefined);
      setExternallySetLastLocation(undefined);
      setExpandEventCategory(undefined);

      if (somethingSelected) {
        setRefreshSearch(true);
      }
    },
    [deselectAllEntities, somethingSelected],
  );

  // Add most recentFenceEvents to the selected geofence -> activeGeofence.
  const selectedGeofenceId = getSelectedGeofence(selected)?.id;
  useEffect(
    function syncActiveGeofenceRecentEvents() {
      if (!selectedGeofenceId) {
        setActiveGeofence(undefined);
        return;
      }
      const effectTimoutId = setTimeout(() => {
        const recentFenceEvents = (
          (
            activeAssets
              .toArray()
              .flatMap(asset => asset.recentFenceEvents)
              .filter(isDefined)
              .filter(recentFenceEvent => recentFenceEvent?.fenceIds.id === selectedGeofenceId) ??
            []
          ).sort((a, b) => compareDesc(new Date(a.iso8601), new Date(b.iso8601))) ?? []
        )?.slice(0, 20);
        setActiveGeofence({ recentFenceEvents });
      }, 1000);
      return () => {
        if (effectTimoutId) {
          clearTimeout(effectTimoutId);
        }
      };
    },
    [activeAssets, selectedGeofenceId, lastStreamed],
  );

  // Update active assets with corresponding microfences
  useEffect(
    function attachMicrofencesToActiveAssets() {
      if (microfences.length === 0 || activeAssets.size === 0) return;
      setMicrofencetoAssets(
        activeAssets,
        microfences.map(microfence => microfence.data),
      );
      setActiveMicrofences(activeAssets, microfences);
    },
    [lastUpdate, microfences, activeAssets],
  );
  // Keep the selection's active asset in sync with the live stream: resolve it
  // when an asset/microfence is first selected, and refresh it on each stream
  // update. Returns the same reference when nothing changed to avoid re-renders.
  useEffect(
    function reconcileSelectedActiveAsset() {
      setSelected(prev => {
        if (!prev) return prev;

        const nextActiveAsset = (
          found?: AssetState,
          current?: AssetState,
        ): AssetState | undefined => {
          if (!found) return undefined;
          if (
            current &&
            stringifyIdRecord(found.id) === stringifyIdRecord(current.id) &&
            found.lastUpdate === current.lastUpdate
          ) {
            return undefined;
          }
          return { ...found, selected: true };
        };

        let found: AssetState | undefined;
        switch (prev.type) {
          case EntityType.Beacon:
          case EntityType.Device:
          case EntityType.GpsTracker:
          case EntityType.Tool:
            found = activeAssets.get(
              typeAndIdToAssetStateKey(prev.data.asset.type, prev.data.asset.id),
            );
            break;
          case EntityType.Microfence:
            found = activeAssets.get(stringifyIdRecord(prev.data.microfence.assetId));
            break;
          case EntityType.Asset:
            found = activeAssets.get(stringifyIdRecord(prev.data.activeAsset.id));
            break;
          default:
            return prev;
        }

        const next = nextActiveAsset(found, getSelectedActiveAsset(prev));
        return next ? withActiveAsset(prev, next) : prev;
      });
    },
    [activeAssets, lastUpdate, lastStreamed, selected],
  );

  const getNowIso8601 = useCallback(function getNowIso8601() {
    return new Date().toISOString();
  }, []);

  const onClickedAssetEventLink = useCallback(
    async function onClickedAssetEventLink(
      identifiersAndLabel: IdentifiersAndLabel | undefined,
      geofence?: ProjectlessFenceIdentifiers & { layerId: string },
    ) {
      // Changing selection through an event hyperlink must not reuse a pending
      // explicit navigation request from the previously selected entity.
      setNavigate(false);
      select(undefined);
      setExternallySetLastLocation(undefined);
      setExpandEventCategory(undefined);

      if (geofence) {
        const foundGeofence = await getGeofence(geofence);
        if (!foundGeofence) return;

        setSearchType({ id: SearchTypeIDs.Geofences, value: SearchTypeValue.Geofences });
        setSelectedLayer({
          id: geofence?.layerId,
          name: layers.find(lyr => lyr.id === geofence?.layerId)?.name ?? UNKNOWN,
        });
        setTimeout(() => {
          setClearFilter(true);
          select(
            geofenceSelection({
              ...foundGeofence,
              layerId: geofence.layerId,
              type: foundGeofence.points?.type,
              zone:
                geometryTypeOfEntity(foundGeofence) === FenceEntityType.Multipolygon
                  ? FenceZone.cleared
                  : foundGeofence.zone,
              selected: true,
            }),
          );
        });
        return;
      }
      if (!identifiersAndLabel) return;

      const activeAsset =
        activeAssets.size > 0
          ? activeAssets.get(stringifyIdRecord(identifiersAndLabel.id))
          : undefined;

      const foundMicrofence =
        activeAsset?.microfence ??
        microfences.find(
          microfence =>
            stringifyIdRecord(microfence.feature.get('assetId')) ===
            stringifyIdRecord(identifiersAndLabel.id),
        );
      if (foundMicrofence) {
        select(microfenceSelection(foundMicrofence, activeAsset));
        return;
      }

      const foundTool =
        activeAsset?.tool ??
        knownTools.find(tool => tool.id === stringifyIdRecord(identifiersAndLabel.id));

      if (identifiersAndLabel.id.beaconId) {
        select(
          foundTool
            ? toolSelection(foundTool, activeAsset)
            : assetSelection(
                EntityType.Beacon,
                {
                  id: identifiersAndLabel.id.beaconId,
                  label: identifiersAndLabel.label,
                  type: AssetEntity.Beacon,
                },
                activeAsset,
              ),
        );
      } else if (identifiersAndLabel.id.deviceId) {
        select(
          assetSelection(
            EntityType.Device,
            {
              id: identifiersAndLabel.id.deviceId,
              label: identifiersAndLabel.label,
              type: AssetEntity.Device,
            },
            activeAsset,
          ),
        );
      } else if (identifiersAndLabel.id.gpsTrackerId) {
        select(
          assetSelection(
            EntityType.GpsTracker,
            {
              id: identifiersAndLabel.id.gpsTrackerId,
              label: identifiersAndLabel.label,
              type: AssetEntity.GpsTracker,
            },
            activeAsset,
          ),
        );
      }
    },
    [activeAssets, getGeofence, knownTools, layers, microfences, select],
  );

  const value = useMemo(
    () => ({
      activeAssets,
      activeGeofence,
      setActiveGeofence,
      assetFilter,
      clearFilter,
      currentCenter,
      deselectFence,
      expandEventCategory,
      externallySetLastLocation,
      fencesLoading,
      geofenceFilter,
      getGeofence,
      getNowIso8601,
      lastUpdate,
      lastStreamed,
      layers,
      locationDisplay,
      locationSearchData,
      liveMapStaticData,
      mapState,
      setMapState,
      mapType,
      microfenceFilter,
      microfences,
      navigate,
      onCategorySelected,
      onClickedAssetEventLink,
      onClickedBack,
      onEntitySelected,
      onExtentChanged,
      range,
      refreshSearch,
      resetStateRef,
      searchType,
      selected,
      select,
      selectedLayer,
      toolFilter,
      setAssetFilter,
      setClearFilter,
      setCurrentCenter,
      setDeselectFence,
      setExpandEventCategory,
      setExternallySetLastLocation,
      setGeofenceFilter,
      setLocationDisplay,
      setLocationSearchData,
      setMicrofenceFilter,
      setNavigate,
      setRange,
      setRefreshSearch,
      setSearchType,
      setSelectedActiveAsset,
      setSelectedLayer,
      selectedStringifiedGeofenceEventData,
      setSelectedStringifiedGeofenceEventData,
      setShowFilter,
      setToolFilter,
      setUserExtent,
      showFilter,
      userExtent,
    }),
    [
      activeAssets,
      activeGeofence,
      setActiveGeofence,
      assetFilter,
      clearFilter,
      currentCenter,
      deselectFence,
      expandEventCategory,
      externallySetLastLocation,
      fencesLoading,
      geofenceFilter,
      getGeofence,
      getNowIso8601,
      lastUpdate,
      lastStreamed,
      layers,
      locationDisplay,
      locationSearchData,
      liveMapStaticData,
      mapState,
      setMapState,
      mapType,
      microfenceFilter,
      microfences,
      navigate,
      onCategorySelected,
      onClickedAssetEventLink,
      onClickedBack,
      onEntitySelected,
      onExtentChanged,
      range,
      refreshSearch,
      resetStateRef,
      searchType,
      selected,
      select,
      selectedLayer,
      toolFilter,
      setAssetFilter,
      setClearFilter,
      setCurrentCenter,
      setDeselectFence,
      setExpandEventCategory,
      setExternallySetLastLocation,
      setGeofenceFilter,
      setLocationDisplay,
      setLocationSearchData,
      setMicrofenceFilter,
      setNavigate,
      setRange,
      setRefreshSearch,
      setSearchType,
      setSelectedActiveAsset,
      setSelectedLayer,
      selectedStringifiedGeofenceEventData,
      setSelectedStringifiedGeofenceEventData,
      setShowFilter,
      setToolFilter,
      setUserExtent,
      showFilter,
      userExtent,
    ],
  );

  return <LiveReplayMapContext.Provider value={value}>{children}</LiveReplayMapContext.Provider>;
};

export const useLiveReplayMap = () => useContext(LiveReplayMapContext);
